import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { driverApplicationMatchesSearch } from '../lib/admin-driver-search';
import {
  fetchDriverRatingReport,
  ratingLabel,
  summaryMatchesFilter,
  updateDriverRatingAdminAction,
} from '../lib/admin-driver-ratings';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const PAGE_SIZES = [10, 15, 25];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All approved drivers' },
  { value: 'reviewed', label: 'Has rider reviews' },
  { value: 'below4', label: 'Average below 4.0' },
  { value: 'below3', label: 'Average below 3.0' },
  { value: 'attention', label: '1–2★ in last 30 days' },
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  return phone || '—';
}

function statusClass(status) {
  return `admin-status admin-status--${status || 'pending'}`;
}

function buildPageList(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

function StarRating({ value, showNumeric = true }) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return <span className="admin-rating-stars admin-rating-stars--empty">No reviews yet</span>;
  }
  const rounded = Math.max(1, Math.min(5, Math.round(n)));
  return (
    <span className="admin-rating-stars" title={ratingLabel(rounded)}>
      <span className="admin-rating-stars__glyph" aria-hidden="true">
        {'★'.repeat(rounded)}
        {'☆'.repeat(5 - rounded)}
      </span>
      {showNumeric ? (
        <span className="admin-rating-stars__num">{n.toFixed(2)}</span>
      ) : null}
    </span>
  );
}

function AdminDriverRatingsPage() {
  const navigate = useNavigate();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('reviewed');
  const [view, setView] = useState('drivers');
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [saving, setSaving] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError('');
    const { report: nextReport, error: loadError } = await fetchDriverRatingReport(supabase);
    setReport(nextReport);
    setError(loadError || '');
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const selectedSummary = useMemo(() => {
    if (!report || !selectedDriverId) return null;
    return report.summaries.find((row) => row.driver.id === selectedDriverId) || null;
  }, [report, selectedDriverId]);

  useEffect(() => {
    setNotesDraft(String(selectedSummary?.driver?.admin_notes || ''));
  }, [selectedSummary?.driver?.id, selectedSummary?.driver?.admin_notes]);

  const filteredSummaries = useMemo(() => {
    if (!report) return [];
    return report.summaries.filter((row) => {
      if (!summaryMatchesFilter(row, filter)) return false;
      return driverApplicationMatchesSearch(row.driver, search);
    });
  }, [report, filter, search]);

  const filteredRecent = useMemo(() => {
    if (!report) return [];
    return report.recentReviews.filter((row) => {
      if (filter === 'reviewed' && !row.stars) return false;
      if (filter === 'below4' && row.stars >= 4) return false;
      if (filter === 'below3' && row.stars >= 3) return false;
      if (filter === 'attention') {
        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        if (row.stars > 2 || Date.parse(row.ratedAt || 0) < thirtyDaysAgo) return false;
      }
      return driverApplicationMatchesSearch(row.driver, search);
    });
  }, [report, filter, search]);

  useEffect(() => {
    setPage(1);
  }, [filter, search, pageSize, view]);

  const list = view === 'drivers' ? filteredSummaries : filteredRecent;
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, currentPage, pageSize]);

  const rangeStart = list.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, list.length);

  const patchDriverInReport = (updatedDriver) => {
    if (!updatedDriver || !report) return;
    setReport((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        summaries: prev.summaries.map((row) =>
          row.driver.id === updatedDriver.id ? { ...row, driver: updatedDriver } : row
        ),
        recentReviews: prev.recentReviews.map((row) =>
          row.driver.id === updatedDriver.id ? { ...row, driver: updatedDriver } : row
        ),
      };
    });
  };

  const runAdminAction = async (payload) => {
    if (!supabase || !selectedSummary?.driver?.id) return;
    setSaving(true);
    setError('');
    const { data, error: actionError } = await updateDriverRatingAdminAction(
      supabase,
      selectedSummary.driver.id,
      payload
    );
    setSaving(false);
    if (actionError) {
      setError(actionError);
      return;
    }
    patchDriverInReport(data);
  };

  const saveNotes = () =>
    runAdminAction({
      admin_notes: notesDraft.trim() || null,
    });

  const setStatus = (status) =>
    runAdminAction({
      status,
      admin_notes: notesDraft.trim() || null,
      reviewed_at: new Date().toISOString(),
    });

  if (!sessionChecked) {
    return null;
  }

  return (
    <AdminShell userEmail={user?.email} active="ratings" onLogout={() => supabase?.auth.signOut()}>
      <div className="admin-page admin-ratings">
        <header className="admin-page__head">
          <div>
            <h1>Driver ratings</h1>
            <p className="admin-page__lede">
              True averages from rider star ratings after trips. Use low scores and feedback to
              coach drivers or change their status.
            </p>
          </div>
          <button type="button" className="btn btn--ghost-dark" onClick={() => load()} disabled={loading}>
            Refresh
          </button>
        </header>

        <div className="admin-ratings-kpis">
          <div className="admin-referrals-kpi">
            <strong>{report?.totals.driversWithReviews ?? (loading ? '…' : 0)}</strong>
            <span>Drivers with reviews</span>
          </div>
          <div className="admin-referrals-kpi">
            <strong>{report?.totals.totalReviews ?? (loading ? '…' : 0)}</strong>
            <span>Total rider reviews</span>
          </div>
          <div className="admin-referrals-kpi admin-ratings-kpi--warn">
            <strong>{report?.totals.driversBelowFour ?? (loading ? '…' : 0)}</strong>
            <span>Below 4.0 average</span>
          </div>
          <div className="admin-referrals-kpi admin-ratings-kpi--alert">
            <strong>{report?.totals.driversNeedingAttention ?? (loading ? '…' : 0)}</strong>
            <span>1–2★ in last 30 days</span>
          </div>
        </div>

        {error ? <div className="form-alert form-alert--error">{error}</div> : null}

        <div className="admin-referrals-toolbar admin-ratings-toolbar">
          <div className="admin-referrals-tabs" role="tablist" aria-label="Rating views">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'drivers'}
              className={`admin-referrals-tab ${view === 'drivers' ? 'admin-referrals-tab--active' : ''}`}
              onClick={() => setView('drivers')}
            >
              By driver
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'recent'}
              className={`admin-referrals-tab ${view === 'recent' ? 'admin-referrals-tab--active' : ''}`}
              onClick={() => setView('recent')}
            >
              Recent reviews
            </button>
          </div>
          <label className="admin-ratings-filter">
            <span className="driver-search__label">Filter</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="driver-search-field admin-referrals-search">
            <span className="driver-search__label">Search</span>
            <label className="driver-search">
              <input
                className="driver-search__input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, phone…"
                autoComplete="off"
              />
            </label>
          </div>
          <label className="driver-search__page">
            <span>Show</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span>per page</span>
          </label>
        </div>

        <div className="admin-ratings-layout">
          <div className="admin-drivers__panel admin-ratings-panel">
            {loading ? (
              <p className="admin-empty">Loading rider ratings…</p>
            ) : view === 'drivers' && filteredSummaries.length === 0 ? (
              <p className="admin-empty">No drivers match this filter. Try &ldquo;All approved drivers&rdquo;.</p>
            ) : view === 'recent' && filteredRecent.length === 0 ? (
              <p className="admin-empty">No recent reviews match this filter.</p>
            ) : (
              <>
                <div className="admin-drivers__table-wrap">
                  <table className="admin-drivers-table">
                    <thead>
                      <tr>
                        {view === 'drivers' ? (
                          <>
                            <th scope="col">Driver</th>
                            <th scope="col">True average</th>
                            <th scope="col">Reviews</th>
                            <th scope="col">App profile ★</th>
                            <th scope="col">Low (≤2★)</th>
                            <th scope="col">Last review</th>
                            <th scope="col">Status</th>
                          </>
                        ) : (
                          <>
                            <th scope="col">When</th>
                            <th scope="col">Driver</th>
                            <th scope="col">Stars</th>
                            <th scope="col">Feedback</th>
                            <th scope="col">Trip</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {view === 'drivers'
                        ? pageRows.map((row) => (
                            <tr
                              key={row.driver.id}
                              className={`admin-table-row ${
                                selectedDriverId === row.driver.id ? 'admin-table-row--selected' : ''
                              }`}
                              tabIndex={0}
                              onClick={() => setSelectedDriverId(row.driver.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedDriverId(row.driver.id);
                                }
                              }}
                            >
                              <td>
                                <strong>{row.driver.full_name}</strong>
                                <div className="admin-table-sub">{formatPhone(row.driver.phone)}</div>
                              </td>
                              <td>
                                <StarRating value={row.averageRating} />
                                {row.ratingMismatch ? (
                                  <div className="admin-ratings-mismatch" title="Differs from stored profile rating">
                                    Mismatch
                                  </div>
                                ) : null}
                              </td>
                              <td>{row.reviewCount || '—'}</td>
                              <td>
                                {row.storedRating != null ? (
                                  <span className="admin-ratings-profile-star">
                                    {row.storedRating.toFixed(2)} ★
                                    {row.profileTrips != null ? (
                                      <span className="admin-table-sub"> · {row.profileTrips} trips</span>
                                    ) : null}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>{row.lowRatingCount || '—'}</td>
                              <td>{formatDate(row.lastRatedAt)}</td>
                              <td>
                                <span className={statusClass(row.driver.status)}>{row.driver.status}</span>
                              </td>
                            </tr>
                          ))
                        : pageRows.map((row) => (
                            <tr
                              key={`${row.rideId}-${row.ratedAt}`}
                              className={`admin-table-row ${
                                selectedDriverId === row.driver.id ? 'admin-table-row--selected' : ''
                              }`}
                              tabIndex={0}
                              onClick={() => setSelectedDriverId(row.driver.id)}
                            >
                              <td>{formatDate(row.ratedAt)}</td>
                              <td>
                                <strong>{row.driver.full_name}</strong>
                              </td>
                              <td>
                                <StarRating value={row.stars} showNumeric={false} />
                                <span className="admin-table-sub">{ratingLabel(row.stars)}</span>
                              </td>
                              <td className="admin-ratings-feedback">
                                {row.feedback || <em>No comment</em>}
                              </td>
                              <td>
                                <Link
                                  to={`/admin/instant-rides?view=completed&q=${encodeURIComponent(row.rideId)}`}
                                  className="admin-ratings-trip-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row.rideId.slice(0, 8)}…
                                </Link>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                {list.length > 0 ? (
                  <footer className="admin-drivers__pager">
                    <p>
                      Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
                      <strong>{list.length}</strong>
                    </p>
                    <nav aria-label="Pagination">
                      {buildPageList(currentPage, totalPages).map((item, index) =>
                        item === '…' ? (
                          <span key={`gap-${index}`} className="admin-drivers__pager-gap">
                            …
                          </span>
                        ) : (
                          <button
                            key={item}
                            type="button"
                            className={`admin-drivers__pager-btn ${
                              item === currentPage ? 'admin-drivers__pager-btn--active' : ''
                            }`}
                            onClick={() => setPage(item)}
                          >
                            {item}
                          </button>
                        )
                      )}
                    </nav>
                  </footer>
                ) : null}
              </>
            )}
          </div>

          <aside className="admin-ratings-detail" aria-label="Driver rating detail">
            {!selectedSummary ? (
              <div className="admin-ratings-detail__empty">
                Select a driver to see individual reviews and take action.
              </div>
            ) : (
              <>
                <header className="admin-ratings-detail__head">
                  <h2>{selectedSummary.driver.full_name}</h2>
                  <p>{formatPhone(selectedSummary.driver.phone)}</p>
                  <div className="admin-ratings-detail__scores">
                    <div>
                      <span className="admin-ratings-detail__label">True average (riders)</span>
                      <StarRating value={selectedSummary.averageRating} />
                      <span className="admin-table-sub">
                        {selectedSummary.reviewCount} review
                        {selectedSummary.reviewCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div>
                      <span className="admin-ratings-detail__label">Shown in app</span>
                      <strong>
                        {selectedSummary.storedRating != null
                          ? `${selectedSummary.storedRating.toFixed(2)} ★`
                          : '—'}
                      </strong>
                    </div>
                  </div>
                </header>

                <section className="admin-ratings-detail__reviews">
                  <h3>Recent rider feedback</h3>
                  {selectedSummary.reviews.length === 0 ? (
                    <p className="admin-empty">No rider reviews recorded yet.</p>
                  ) : (
                    <ul className="admin-ratings-review-list">
                      {selectedSummary.reviews.slice(0, 15).map((review) => (
                        <li key={`${review.rideId}-${review.ratedAt}`}>
                          <div className="admin-ratings-review-list__top">
                            <StarRating value={review.stars} showNumeric={false} />
                            <time dateTime={review.ratedAt}>{formatDate(review.ratedAt)}</time>
                          </div>
                          <p>{review.feedback || 'No written feedback.'}</p>
                          {review.pickup || review.dropoff ? (
                            <p className="admin-table-sub">
                              {[review.pickup, review.dropoff].filter(Boolean).join(' → ')}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="admin-ratings-detail__actions">
                  <h3>Admin actions</h3>
                  <label className="admin-ratings-notes">
                    <span>Internal notes / coaching feedback</span>
                    <textarea
                      rows={4}
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      placeholder="Document follow-up, warnings, or praise for this driver."
                    />
                  </label>
                  <div className="admin-ratings-action-row">
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      disabled={saving}
                      onClick={() => saveNotes()}
                    >
                      Save notes
                    </button>
                    <Link
                      to="/admin"
                      state={{ selectDriverId: selectedSummary.driver.id }}
                      className="btn btn--ghost-dark"
                    >
                      Open application
                    </Link>
                  </div>
                  <div className="admin-ratings-action-row admin-ratings-action-row--status">
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      disabled={saving}
                      onClick={() => setStatus('reviewing')}
                    >
                      Mark reviewing
                    </button>
                    <button
                      type="button"
                      className="btn btn--deactivate"
                      disabled={saving}
                      onClick={() => setStatus('deactivated')}
                    >
                      Deactivate driver
                    </button>
                  </div>
                </section>
              </>
            )}
          </aside>
        </div>
      </div>
    </AdminShell>
  );
}

export default AdminDriverRatingsPage;
